import SEO from "@/components/SEO";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

export default function LoginPage() {
  const [aba, setAba] = useState<"login" | "cadastro">("login");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  const [telefone, setTelefone] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [sucesso, setSucesso] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    setCarregando(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password: senha });
    setCarregando(false);
    if (error) {
      setErro(error.message === "Invalid login credentials" ? "Email ou senha incorretos." : error.message);
    } else {
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get("redirect") || "/minha-conta";
      navigate(redirect);
    }
  };

  function formatCpf(v: string) {
    const digits = v.replace(/\D/g, "").slice(0, 11);
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  function formatPhone(v: string) {
    const digits = v.replace(/\D/g, "").slice(0, 11);
    if (digits.length > 6) {
      return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    }
    if (digits.length > 2) {
      return digits.replace(/(\d{2})(\d*)/, "($1) $2");
    }
    return digits;
  }

  function validarCadastro(): string | null {
    if (!nome.trim()) return "Nome completo é obrigatório.";
    if (nome.trim().split(" ").length < 2) return "Informe seu nome completo (nome e sobrenome).";
    const cpfDigits = cpf.replace(/\D/g, "");
    if (cpfDigits.length < 11) return "CPF completo é obrigatório.";
    if (!dataNascimento) return "Data de nascimento é obrigatória.";
    const hoje = new Date();
    const nasc = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nasc.getFullYear();
    const m = hoje.getMonth() - nasc.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
    if (idade < 13) return "Você deve ter pelo menos 13 anos para se cadastrar.";
    const telDigits = telefone.replace(/\D/g, "");
    if (telDigits.length < 10) return "Celular completo é obrigatório.";
    if (!email.trim() || !email.includes("@")) return "E-mail válido é obrigatório.";
    if (senha.length < 6) return "A senha deve ter pelo menos 6 caracteres.";
    if (senha !== confirmarSenha) return "As senhas não coincidem.";
    return null;
  }

  const handleCadastro = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    const validationError = validarCadastro();
    if (validationError) {
      setErro(validationError);
      return;
    }

    setCarregando(true);

    const nameParts = nome.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || null;

    // CORREÇÃO: passa os dados extras no metadata do signUp.
    // O Supabase armazena isso em auth.users.raw_user_meta_data.
    // O trigger de criação de perfil (ou a lógica pós-confirmação) pode lê-los de lá.
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password: senha,
      options: {
        data: {
          nome: firstName,
          sobrenome: lastName,
          cpf: cpf.replace(/\D/g, ""),
          data_nascimento: dataNascimento,
          telefone: telefone.replace(/\D/g, ""),
          email_contato: email,
        },
      },
    });

    if (signUpError) {
      setCarregando(false);
      setErro(signUpError.message);
      return;
    }

    const userId = signUpData?.user?.id;

    // identities vazio = e-mail já cadastrado aguardando confirmação
    const emailJaCadastrado =
      signUpData?.user?.identities && signUpData.user.identities.length === 0;

    if (emailJaCadastrado) {
      setCarregando(false);
      setErro("Este e-mail já está cadastrado. Verifique sua caixa de entrada ou tente fazer login.");
      return;
    }

    if (userId) {
      // CORREÇÃO PRINCIPAL: upsert em vez de update.
      // Se o trigger ainda não criou a linha (confirmação pendente), o upsert a cria.
      // Se já existe, atualiza. Evita a race condition.
      const { error: profileError } = await supabase
        .from("profiles")
        .upsert(
          {
            id: userId,
            nome: firstName,
            sobrenome: lastName,
            cpf: cpf.replace(/\D/g, ""),
            data_nascimento: dataNascimento,
            telefone: telefone.replace(/\D/g, ""),
            email_contato: email,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "id" }
        );

      if (profileError) {
        // Não bloqueia o fluxo — os dados estão salvos no metadata do Auth como fallback.
        // O trigger pode sincronizá-los depois da confirmação.
        console.error("Erro ao salvar perfil:", profileError.message);
      }
    }

    // Verifica se o Supabase está exigindo confirmação de e-mail
    const precisaConfirmar = !signUpData?.session;

    setCarregando(false);

    if (precisaConfirmar) {
      // Confirmação de e-mail ativada no Supabase
      setSucesso(
        "Conta criada! Enviamos um e-mail de confirmação para " +
          email +
          ". Clique no link para ativar sua conta e fazer login."
      );
    } else {
      // Confirmação desativada — sessão já está ativa
      navigate("/meu-perfil");
    }
  };

  return (
    <>
      <SEO
        title="Entrar na Minha Conta"
        description="Faça login ou crie sua conta na GodoyProd para acessar suas partituras compradas, playlists e comunidade de músicos."
        keywords="login, criar conta, GodoyProd, acessar partituras"
        noindex
        canonicalPath="/login"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        <section className="pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-lg mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 mx-auto mb-4">
                <img
                  src="https://pub-bc092def332d4fb180f02b510802da8e.r2.dev/LogoGP.png"
                  alt="GodoyProd"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="font-serif text-3xl font-bold text-godoy-cream">
                {aba === "login" ? "Entrar" : "Criar Conta"}
              </h1>
              <p className="text-godoy-lightgray text-sm mt-2">
                {aba === "login"
                  ? "Acesse suas partituras compradas"
                  : "Cadastre-se para comprar partituras"}
              </p>
            </div>

            {/* Abas */}
            <div className="flex bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg p-1 mb-8">
              <button
                onClick={() => { setAba("login"); setErro(""); setSucesso(""); }}
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
                  aba === "login"
                    ? "bg-godoy-gold text-godoy-black"
                    : "text-godoy-lightgray hover:text-godoy-cream"
                }`}
              >
                Entrar
              </button>
              <button
                onClick={() => { setAba("cadastro"); setErro(""); setSucesso(""); }}
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
                  aba === "cadastro"
                    ? "bg-godoy-gold text-godoy-black"
                    : "text-godoy-lightgray hover:text-godoy-cream"
                }`}
              >
                Criar Conta
              </button>
            </div>

            {erro && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">
                {erro}
              </div>
            )}
            {sucesso && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-emerald-400 text-sm">
                {sucesso}
              </div>
            )}

            {/* Form */}
            <div className="bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg p-6 md:p-8">
              {aba === "login" ? (
                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="block text-sm text-godoy-lightgray mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      placeholder="seu@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-godoy-lightgray mb-1.5">
                      Senha
                    </label>
                    <input
                      type="password"
                      required
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      placeholder="Sua senha"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={carregando}
                    className="w-full py-3 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60"
                  >
                    {carregando ? "Entrando..." : "Entrar"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleCadastro} className="space-y-5">
                  <div>
                    <label className="block text-sm text-godoy-lightgray mb-1.5">
                      Nome Completo <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">
                        CPF <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={cpf}
                        onChange={(e) => setCpf(formatCpf(e.target.value))}
                        placeholder="000.000.000-00"
                        maxLength={14}
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">
                        Data de Nascimento <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={dataNascimento}
                        onChange={(e) => setDataNascimento(e.target.value)}
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">
                        Celular / WhatsApp <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={telefone}
                        onChange={(e) => setTelefone(formatPhone(e.target.value))}
                        placeholder="(15) 9 9181-1212"
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">
                        Email <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                        placeholder="seu@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-godoy-lightgray mb-1.5">
                      Senha <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="password"
                      required
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      placeholder="Crie uma senha (mín. 6 caracteres)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-godoy-lightgray mb-1.5">
                      Confirmar senha <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="password"
                      required
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      placeholder="Repita a senha"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={carregando}
                    className="w-full py-3 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60"
                  >
                    {carregando ? "Criando conta..." : "Criar Conta"}
                  </button>
                </form>
              )}
            </div>

            <p className="text-center text-godoy-lightgray text-xs mt-6">
              Ao entrar, você terá acesso às suas partituras compradas e poderá visualizá-las em tela cheia.
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}